import { useState } from "react";
import useGet from "../read/useGet";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { deleteLeaveManagement } from "../../api/holiday";
import usePut from "../usePut";

const useLeaveStructure = ({ id, year }) => {
  const queryClient = useQueryClient();
  const [pageNo, setPageNo] = useState(0);
  
  // First date of the year
  const firstDate = new Date(year.getFullYear(), 0, 1); // January is month 0
  // Last date of the year
  const lastDate = new Date(year.getFullYear(), 11, 31, 23, 59, 59, 999); // December is month 11
  
  // API loading states for operations
  const [isAddingData, setAddingData] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Delete confirmation
  const [leaveManagementId, setLeaveManagementId] = useState("");
  const [leaveManagementRecord, setLeaveManagementRecord] = useState(null);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [addLeaveManagementModal, setAddLeaveManagementModal] = useState(false);

  // Initial data for API calls
  let initialLeaveManagementData = {
    limit: 10,
    page_no: 0,
    month_year: year?.getFullYear(),
    company_employee_id: id,
  };

  // Data fetching for leave management listing
  const {
    list: leaveManagementList,
    paramsObject,
    setGetListParams,
  } = useGet({
    url: "fetchEmployeeLeaveStructure",
    method: "get",
    initialData: initialLeaveManagementData,
  });
  // const {
  //   list: leaveManagementList,
  //   paramsObject,
  //   setGetListParams,
  // } = useGet({
  //   url: "getUserLeaveManagementLsiting",
  //   method: "get",
  //   initialData: initialLeaveManagementData,
  // });

  // Data fetching for user data
  const {
    list: userdata,
    paramsObject: userParams,
  } = useGet({
    url: "getUserData",
    method: "get",
    initialData: {
      company_employee_id: id,
      ...initialLeaveManagementData,
    },
  });

  // Fetch leave structure types
  const {
    list: leaveStructureData,
    isLoading: isLeaveStructureLoading,
  } = useGet({
    url: "fetchLeaveStructure",
    method: "get",
    initialData: {},
  });

  // Loading states
  // const isLeaveManagementListLoading = leaveManagementList?.isLoading || leaveManagementList?.isFetching;
  const isLeaveManagementListLoading = leaveManagementList?.isLoading;
  const isUserDataLoading = userdata?.isLoading;
  const isAnyDataLoading = isLeaveManagementListLoading || isUserDataLoading || isLeaveStructureLoading;

  // Calculate available leaves
  const calculateAvailableLeaves = (annualLeaves, dateOfJoiningRaw) => {
    // Convert seconds to milliseconds and parse the date
    let parsedDateOfJoining = dateOfJoiningRaw
      ? new Date(dateOfJoiningRaw * 1000) // Multiply by 1000
      : null;

    // Validate parsed date
    if (!parsedDateOfJoining || isNaN(parsedDateOfJoining)) {
      console.error("Invalid Date of Joining:", dateOfJoiningRaw);
      return annualLeaves; // Return annual leaves if date is invalid
    }

    // Get the first date of the current year
    const firstDate = new Date(new Date().getFullYear(), 0, 1);

    // Validate annual leaves
    if (!annualLeaves || isNaN(annualLeaves)) {
      console.error("Invalid Annual Leaves:", annualLeaves);
      return 0; // Return 0 if annual leaves are invalid
    }

    // Perform the pro-rata leave calculation
    if (parsedDateOfJoining.getFullYear() !== firstDate.getFullYear()) {
      return annualLeaves;
    } else {
      const leavesInOneMonth = annualLeaves / 12;
      const monthsDifference =
        12 - (parsedDateOfJoining.getMonth() - firstDate.getMonth());
      return Math.floor(leavesInOneMonth * monthsDifference);
    }
  };

  // Create leave management
  const { handleAuth: createLeaveManagement } = usePut({
    url: "createUserLeaveManagement",
    refreshUrl: "getUserLeaveManagementLsiting",
    onSuccess: () => {
      toast.success("Leave management created successfully");
      setAddingData(false);
      setAddLeaveManagementModal(false);
    },
    onError: (error) => {
      toast.error("Failed to create leave management");
      setAddingData(false);
      console.error("Leave management creation error:", error);
    }
  });

  // Helper function to format dates as DD/MM/YYYY
  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Handle page change for pagination
  const handlePageClick = (val) => {
    let data = {
      ...paramsObject,
      page_no: val.selected,
    };
    setPageNo(val.selected);
    setGetListParams(data);
  };

  // Delete leave management
  const handleOpenDeleteModal = (id) => {
    const leaveRecord = leaveManagementList?.data?.data.find(item => item.id === id);
    setLeaveManagementRecord(leaveRecord);
    setDeleteConfirmationModal(true);
  };

  const handleDeleteLeave = () => {
    setIsDeleting(true);
    deleteLeaveManagement(
      leaveManagementRecord.id, 
      leaveManagementRecord.company_id, 
      leaveManagementRecord.company_employee_id
    )
      .then((res) => {
        if (res.status === 200) {
          setDeleteConfirmationModal(false);
          toast.success("Deleted Successfully");
          queryClient.invalidateQueries(["getUserLeaveManagementLsiting"]);
          setIsDeleting(false);
        }
      })
      .catch((err) => {
        toast.error("Something went wrong!");
        setIsDeleting(false);
      });
  };

  // Submit leave management form
  const submitLeaveManagementForm = (leaveStructure) => {
    setAddingData(true);
    
    const payload = {
      company_employee_id: id,
      leave_structure: leaveStructure
    };
    createLeaveManagement.mutate(payload);
  };

  return {
    // States
    isLoading: isLeaveManagementListLoading,
    isAddingData,
    isDeleting,
    pageNo,
    leaveManagementId,
    leaveManagementRecord,
    firstDate,
    lastDate,
    deleteConfirmationModal,
    isLeaveStructureLoading,
    isUserDataLoading,
    isAnyDataLoading,
    addLeaveManagementModal,
    
    // Data
    leaveManagementList: leaveManagementList,
    userdata,
    paramsObject,
    leaveStructureData,
    
    // Functions
    formatDate,
    handlePageClick,
    handleOpenDeleteModal,
    handleDeleteLeave,
    submitLeaveManagementForm,
    setDeleteConfirmationModal,
    setAddLeaveManagementModal,
    calculateAvailableLeaves,
  };
};

export default useLeaveStructure;
